import React, { useEffect, useState } from "react";
import { COMPLIANT_LABEL } from "src/constants/constants";
import { Button, Column, Icon, Row, Sheet, Tab, TabGroup, Text } from "@amzn/meridian";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import { InspectionResult, isOverridden } from "src/services/inspectionService";
import ThemedTag from "./ThemedTag";
import OverrideIcon from "./OverrideIcon";
import "./ComplianceDetailSheet.scss";

interface CountDetailSheetProps {
    open: boolean;
    onClose: () => void;
    result: InspectionResult | null;
    onOverride?: (result: InspectionResult) => void;
    /** Human-readable question text from the workflow question config. */
    questionText?: string;
}

/**
 * Detail sheet for auto-calculated count results (today: hazardous container
 * count). A count result has no evidence images — its "original" value is the
 * system-computed `calculatedValue` and, once an inspector overrides it, the
 * corrected value lives in `confirmedResult`.
 *
 * When overridden the sheet shows two text tabs (Overridden / Original) with the
 * count and its tags per tab. When not overridden it shows the single computed
 * value. A corrected count is always treated as compliant, so the compliance tag
 * is always success here.
 */
const CountDetailSheet: React.FC<CountDetailSheetProps> = ({
    open,
    onClose,
    result,
    onOverride,
    questionText,
}) => {
    const [activeTab, setActiveTab] = useState<"overridden" | "original">("overridden");

    useEffect(() => {
        setActiveTab("overridden");
    }, [open, result?.resultId]);

    if (!result) return null;

    const overridden = isOverridden(result);
    const isOriginalTabActive = overridden && activeTab === "original";

    // The original tab shows the system-computed calculatedValue; everywhere else
    // shows confirmedResult (the corrected count when overridden, and equal to
    // calculatedValue when not).
    const answer = isOriginalTabActive ? result.calculatedValue : result.confirmedResult;

    // The original computed value carries its own compliance in originalIsCompliant
    // (falls back to the result's own flag when not present).
    const displayCompliant = isOriginalTabActive && result.originalIsCompliant !== undefined
        ? result.originalIsCompliant
        : result.isCompliant;

    const answerRow = (
        <Row alignmentHorizontal="justify" alignmentVertical="center">
            <Text type="b200" color="primary">Answer : {answer}</Text>
            <Row spacing="200" alignmentVertical="center">
                <ThemedTag
                    text={displayCompliant ? COMPLIANT_LABEL : "Non-Compliant"}
                    type={displayCompliant ? "success" : "error"}
                />
                <ThemedTag text="AUTO" type="neutral" />
            </Row>
        </Row>
    );

    return (
        <Sheet type="overlay" side="bottom" open={open} onClose={onClose} className="compliance-detail-sheet">
            <Column spacing="400" spacingInset="500">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text type="h300">Count Details</Text>
                    <Button onClick={onClose} type="icon">
                        <Icon tokens={closeSmallTokens}>Close count details</Icon>
                    </Button>
                </Row>

                {questionText && (
                    <Text type="b200" color="secondary">
                        {questionText}
                    </Text>
                )}

                {overridden && activeTab !== "original" && (
                    <div style={{ alignSelf: "flex-start" }}>
                        <ThemedTag text="Manually Overridden" type="override" icon={<OverrideIcon />} />
                    </div>
                )}

                {overridden ? (
                    <Column spacing="300">
                        <TabGroup value={activeTab} onChange={setActiveTab} fill="tabs">
                            <Tab value="overridden">Overridden</Tab>
                            <Tab value="original">Original result</Tab>
                        </TabGroup>
                        {answerRow}
                    </Column>
                ) : (
                    answerRow
                )}

                {result.overrideReason && result.overrideReason.trim() !== "" && activeTab !== "original" && (
                    <Column spacing="100">
                        <Text type="h200" color="primary">Comments</Text>
                        <Text type="b200" color="secondary">{result.overrideReason.trim()}</Text>
                    </Column>
                )}

                {onOverride && (
                    <button
                        className="compliance-detail-sheet__override-btn"
                        onClick={() => onOverride(result)}
                    >
                        MANUAL OVERRIDE
                    </button>
                )}
            </Column>
        </Sheet>
    );
};

export default CountDetailSheet;
